import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  CircleMinusIcon,
  CopyIcon,
} from 'lucide-react';
import { useId, useState, type JSX, type ReactNode } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Card, CardContent, CardHeader } from '@/components/ui/card.js';
import {
  artifactTextQueryOptions,
  structuredSeedQueryOptions,
} from '@/lib/api/quality.js';
import type {
  ExploratoryInputSourceSnapshot,
  ExploratoryLaunchSnapshot,
} from '@/lib/api/quality-types.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { cn } from '@/lib/utils.js';

import { parseStructuredSeedPrompt } from './generation-context.js';
import { redactUrlForDisplay } from './generation-trace.js';

type SeedArtifactKeys = { structured: string | null; rendered: string | null };

function toRunRelativeKey(runId: string, key: string): string {
  const prefix = `runs/${runId}/`;
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

export function resolveExploratorySeedKeys(
  runId: string,
  artifactKeys: string[],
): SeedArtifactKeys {
  const candidates = artifactKeys
    .map((key) => {
      const match = /seed-prompt\.v(\d+)\.(json|md)$/.exec(key);
      if (!match) return null;
      const version = Number(match[1]);
      const attemptMatch = /(?:^|\/)attempt-(\d+)\//.exec(key);
      return {
        key,
        version,
        extension: match[2],
        attempt: attemptMatch ? Number(attemptMatch[1]) : -1,
      };
    })
    .filter((candidate) => candidate !== null)
    .filter(
      ({ version, attempt }) =>
        Number.isFinite(version) && Number.isFinite(attempt),
    );
  const attempts = candidates.map(({ attempt }) => attempt);
  const selectedAttempt = attempts.includes(0)
    ? 0
    : Math.max(Number.NEGATIVE_INFINITY, ...attempts);
  let bestVersion = Number.NEGATIVE_INFINITY;
  let structured: string | null = null;
  let rendered: string | null = null;
  for (const { key, version, extension } of candidates
    .filter(({ attempt }) => attempt === selectedAttempt)
    .sort((left, right) => left.key.localeCompare(right.key))) {
    if (version < bestVersion) continue;
    if (version > bestVersion) {
      bestVersion = version;
      structured = null;
      rendered = null;
    }
    const relativeKey = toRunRelativeKey(runId, key);
    if (extension === 'json' && structured === null) structured = relativeKey;
    if (extension === 'md' && rendered === null) rendered = relativeKey;
  }
  return { structured, rendered };
}

function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function MetaRow({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}): JSX.Element | null {
  if (value == null || value === '') return null;
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-x-2 text-[12px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-foreground">{value}</dd>
    </div>
  );
}

function connectorLabel(
  connector: ExploratoryInputSourceSnapshot['connector'],
): string {
  const labels: Record<string, string> = {
    jira: 'Jira',
    testrail: 'TestRail',
    zephyr: 'Zephyr',
    xray: 'Xray',
    azure: 'Azure DevOps',
  };
  return labels[connector] ?? connector;
}

/** Planned routing, decided at launch time — before the worker has done anything. */
function plannedUsageLabel(
  usage: ExploratoryInputSourceSnapshot['usage'],
): string {
  if (usage === 'browsing_prompt') return 'Planned for agent prompt';
  if (usage === 'post_processing') return 'Planned for post-processing';
  return 'Not planned for use';
}

/**
 * What actually happened, per the worker's terminal callback. `undefined` means the run predates
 * this field or has not reached its terminal callback yet — shown as "unknown", never read as
 * proof one way or the other (see `ExploratoryInputSourceSnapshot.actualUsage`'s doc comment).
 */
function actualUsageLabel(
  actualUsage: ExploratoryInputSourceSnapshot['actualUsage'],
): string {
  if (actualUsage === 'post_processing') return 'Used in post-processing';
  if (actualUsage === 'post_processing_attempted')
    return 'Post-processing attempted';
  if (actualUsage === 'not_used') return 'Not used';
  return 'Actual usage unknown';
}

function SourceRow({
  source,
}: {
  source: ExploratoryInputSourceSnapshot;
}): JSX.Element {
  const loaded = source.loadStatus === 'loaded';
  return (
    <li className="grid gap-2 rounded-md border border-border/70 px-3 py-2 text-[12px] sm:grid-cols-[7rem_minmax(7rem,1fr)_auto] sm:items-start">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {loaded ? (
          <CheckCircle2Icon className="size-3.5 text-success-600" aria-hidden />
        ) : (
          <AlertTriangleIcon
            className="size-3.5 text-destructive"
            aria-hidden
          />
        )}
        {connectorLabel(source.connector)}
      </div>
      <div className="min-w-0">
        <p className="break-words font-medium text-foreground">{source.ref}</p>
        {source.failure?.message ? (
          <p className="mt-0.5 text-destructive">{source.failure.message}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-1 sm:justify-end">
        <Badge variant={loaded ? 'success' : 'destructive'}>
          {loaded ? 'Loaded' : 'Unresolved'}
        </Badge>
        {/* An unresolved source was never loaded, so neither planned nor actual usage applies —
            it keeps only the failure message above, never a usage claim of either kind. */}
        {loaded ? (
          <>
            <Badge
              variant={
                source.usage === 'browsing_prompt'
                  ? 'info'
                  : source.usage === 'post_processing'
                    ? 'warning'
                    : 'outline'
              }
            >
              {plannedUsageLabel(source.usage)}
            </Badge>
            <Badge
              variant={
                source.actualUsage === 'post_processing' ? 'success' : 'outline'
              }
            >
              {actualUsageLabel(source.actualUsage)}
            </Badge>
          </>
        ) : null}
      </div>
    </li>
  );
}

function credentialSummary(snapshot: ExploratoryLaunchSnapshot): string {
  if (snapshot.effectiveCredential.kind === 'anonymous') return 'Anonymous';
  if (snapshot.effectiveCredential.kind === 'environment')
    return 'Environment credential';
  if (snapshot.effectiveCredential.kind === 'override')
    return 'Override credential';
  return 'Unknown credential';
}

export function ExploratoryTrace({
  runId,
  environmentName,
  launchSnapshot,
  artifactKeys,
  terminal,
}: {
  runId: string;
  environmentName?: string | null;
  launchSnapshot?: ExploratoryLaunchSnapshot | null;
  artifactKeys: string[];
  terminal: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const bodyId = useId();
  const promptId = useId();
  const seedKeys = resolveExploratorySeedKeys(runId, artifactKeys);
  const seedQuery = useQuery(
    structuredSeedQueryOptions(runId, seedKeys.structured),
  );
  const seed =
    typeof seedQuery.data === 'string'
      ? parseStructuredSeedPrompt(seedQuery.data)
      : null;
  const renderedKey =
    seedKeys.rendered ??
    (seed?.rendered_redacted_key
      ? toRunRelativeKey(runId, seed.rendered_redacted_key)
      : null) ??
    (launchSnapshot ? 'exploration/attempt-0/seed-prompt.v1.md' : null);
  const promptQuery = useQuery({
    ...artifactTextQueryOptions(runId, renderedKey, promptOpen),
    refetchInterval: (query) =>
      !terminal && query.state.data == null && query.state.error == null
        ? 2_000
        : false,
  });

  if (!launchSnapshot && !seedKeys.structured && !seedKeys.rendered)
    return null;

  const sources = launchSnapshot?.input?.sources ?? [];
  const selectedCount = sources.length;
  const loadedCount = sources.filter(
    ({ loadStatus }) => loadStatus === 'loaded',
  ).length;
  const unresolvedCount = selectedCount - loadedCount;
  const promptCount = sources.filter(
    ({ usage }) => usage === 'browsing_prompt',
  ).length;
  const documents = launchSnapshot?.input?.documents ?? [];
  const target = launchSnapshot?.resolvedUrl
    ? redactUrlForDisplay(launchSnapshot.resolvedUrl)
    : null;
  const promptText =
    typeof promptQuery.data === 'string' ? promptQuery.data.trim() : '';

  return (
    <Card>
      <CardHeader className={cn(!open && 'pb-5')}>
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex w-full flex-wrap items-center gap-2 text-left text-sm font-semibold"
        >
          <ChevronDownIcon
            className={cn('size-4 transition-transform', open && 'rotate-180')}
            aria-hidden
          />
          <span>How explored</span>
          {selectedCount > 0 ? (
            <span className="ml-auto text-[11px] font-normal text-muted-foreground">
              {selectedCount} selected · {loadedCount} loaded · {promptCount} in
              prompt
            </span>
          ) : null}
          {unresolvedCount > 0 ? (
            <Badge variant="destructive">{unresolvedCount} unresolved</Badge>
          ) : null}
        </button>
      </CardHeader>
      <CardContent id={bodyId} hidden={!open} className="flex flex-col gap-5">
        {open ? (
          <>
            {launchSnapshot ? (
              <section
                className="flex flex-col gap-2"
                aria-label="Run configuration"
              >
                <SectionTitle>Run configuration</SectionTitle>
                <dl className="flex flex-col gap-1">
                  <MetaRow label="Application" value={target} />
                  <MetaRow
                    label="Environment"
                    value={
                      environmentName ??
                      launchSnapshot.source.environmentName ??
                      null
                    }
                  />
                  <MetaRow
                    label="Platform"
                    value={launchSnapshot.workerSnapshot.platform}
                  />
                  <MetaRow
                    label="Credential"
                    value={credentialSummary(launchSnapshot)}
                  />
                  <MetaRow
                    label="Human-in-the-loop"
                    value={launchSnapshot.effectiveHitl?.hitlState ?? 'Unknown'}
                  />
                  <MetaRow
                    label="Time limit"
                    value={
                      launchSnapshot.input?.maxMinutes
                        ? `${launchSnapshot.input.maxMinutes} minutes`
                        : 'Default'
                    }
                  />
                  <MetaRow
                    label="Focus"
                    value={launchSnapshot.input?.focus ?? null}
                  />
                </dl>
                {documents.length > 0 ? (
                  <div className="text-[12px]">
                    <p className="text-muted-foreground">Documents</p>
                    <ul className="mt-1 flex flex-col gap-0.5">
                      {documents.map((document, index) => (
                        <li key={`${index}-${document.filename}`}>
                          {document.filename}
                          <span className="text-muted-foreground">
                            {' '}
                            · {document.kind}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </section>
            ) : null}

            {sources.length > 0 ? (
              <section
                className="flex flex-col gap-2"
                aria-label="Selected scope"
              >
                <SectionTitle>Selected scope</SectionTitle>
                <ul className="flex flex-col gap-1.5">
                  {sources.map((source) => (
                    <SourceRow
                      key={`${source.kind}-${source.connector}-${source.ref}`}
                      source={source}
                    />
                  ))}
                </ul>
              </section>
            ) : null}

            <section
              className="flex flex-col gap-2"
              aria-label="Prompt used by agent"
            >
              <button
                type="button"
                onClick={() => setPromptOpen((wasOpen) => !wasOpen)}
                aria-expanded={promptOpen}
                aria-controls={promptId}
                className="flex w-fit items-center gap-2 text-left text-[12px] font-medium"
              >
                <ChevronDownIcon
                  className={cn(
                    'size-3.5 transition-transform',
                    promptOpen && 'rotate-180',
                  )}
                  aria-hidden
                />
                <span>Prompt used by agent</span>
                <Badge variant="outline">Redacted</Badge>
              </button>
              <div id={promptId} hidden={!promptOpen}>
                {promptOpen ? (
                  promptQuery.isError ? (
                    <p className="text-[12px] text-destructive">
                      Couldn’t load the redacted prompt.
                    </p>
                  ) : renderedKey && promptQuery.isPending ? (
                    <p className="text-[12px] text-muted-foreground">
                      Loading prompt…
                    </p>
                  ) : promptText ? (
                    <div className="relative">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1"
                        onClick={() =>
                          void copyToClipboard(promptText, 'Prompt copied')
                        }
                      >
                        <CopyIcon /> Copy
                      </Button>
                      <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 pr-20 font-mono text-[11px] text-foreground">
                        {promptText}
                      </pre>
                    </div>
                  ) : (
                    <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <CircleMinusIcon className="size-3.5" aria-hidden />
                      Prompt artifact is not available for this run.
                    </p>
                  )
                ) : null}
              </div>
            </section>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
